import { useState, useEffect } from 'react';
import { useProStudioCtxSelector } from '../xstate/app.xstate';
import { Icon, Tag } from '@pypestream/design-system';

type BotNameValidity = {
  valid: boolean;
  passedLengthCheck: boolean;
  passedUniquenessCheck: boolean;
  passedNameSafetyCheck: boolean;
};

const firstLetterCaps = (name: string) => {
  if (!name) return '';

  const itemName = name.split('');
  itemName[0] = itemName[0].toUpperCase();
  return itemName.join('');
};

const VALID_NAME_REGEX = /^[\p{L}\p{N} .,_\-–—():+/&'’]+$/u;

const isValidSolutionName = (name: string): boolean => {
  const trimmed = name.trim().normalize('NFC');

  return (
    trimmed.length > 0 &&
    trimmed.length <= 100 &&
    VALID_NAME_REGEX.test(trimmed)
  );
};

const solutionNameValidityChecker = (
  stringToValidate: string,
  solutions: string[]
): BotNameValidity => {
  const lengthCheck = stringToValidate.length > 0;
  const uniquenessCheck = solutions.includes(stringToValidate);
  const nameSafetyCheck = isValidSolutionName(stringToValidate);

  return {
    valid: !uniquenessCheck && lengthCheck,
    passedLengthCheck: lengthCheck,
    passedUniquenessCheck: !uniquenessCheck,
    passedNameSafetyCheck: nameSafetyCheck,
  };
};

export const useSolutionNameValidation = (watchedSolutionName: string) => {
  const { solutions } = useProStudioCtxSelector((ctx) => ({
    solutions: (ctx.solutions || [])
      .map((solution) => solution.displayName)
      .filter((name): name is string => Boolean(name)),
  }));

  const [solutionDisplayedName, setSolutionDisplayedName] = useState('');

  const [solutionNameValidityState, setSolutionNameValidityState] =
    useState<BotNameValidity>({
      valid: false,
      passedLengthCheck: false,
      passedUniquenessCheck: false,
      passedNameSafetyCheck: false,
    });

  useEffect(() => {
    if (watchedSolutionName !== solutionDisplayedName) {
      setSolutionDisplayedName(watchedSolutionName || '');
      setSolutionNameValidityState(
        solutionNameValidityChecker(watchedSolutionName || '', solutions)
      );
    }
  }, [watchedSolutionName, solutionDisplayedName, solutions]);

  const transformAndValidateSolutionName = (typedName: string) => {
    const name = firstLetterCaps(typedName);
    setSolutionDisplayedName(name);
    setSolutionNameValidityState(solutionNameValidityChecker(name, solutions));

    return name;
  };

  const isValidName = solutionNameValidityState.valid;
  const isSafetySolutionName =
    !solutionNameValidityState.passedNameSafetyCheck &&
    watchedSolutionName.length > 0;

  const errorMessage = isSafetySolutionName
    ? "Use 1–100 characters. Letters, numbers, spaces, and common symbols like . , - & ' ( ) are allowed"
    : undefined;

  const getTag = (
    checkLabel: string,
    checkKey: keyof Omit<BotNameValidity, 'valid'>
  ) => {
    const isNameEntered = solutionDisplayedName.length > 0;
    const isPassing = solutionNameValidityState[checkKey] && isNameEntered;

    return (
      <Tag
        test-id={`${checkKey}-tag`}
        {...((isNameEntered && {
          variant: isPassing ? 'success' : 'error',
          minimal: true,
          size: 'small',
        }) || {
          variant: 'default',
          size: 'small',
          minimal: true,
        })}
      >
        <Icon name={isPassing ? 'check' : 'close'} />
        {checkLabel}
      </Tag>
    );
  };

  return {
    errorMessage,
    isValidName,
    isSafetySolutionName,
    transformAndValidateSolutionName,
    getTag,
  };
};
